/**
 * MONEY tab — README "Operations tabs · MONEY". WHERE THE MONEY IS (ticket revenue,
 * refunds, the fee lines per funding type, YOURS in green, the ESCROW note with the
 * release moment) and EVERY TRANSACTION from GET /events/{id}/money. While that endpoint
 * is landing the summary is estimated from the applications and the ledger is hidden.
 * The post-event pieces (escrow countdown, REPORT AN ISSUE, host feedback, GUEST
 * FEEDBACK) and the promo codes panel live here too. Built on the host kit (./kit).
 */
import React, { useEffect, useState } from 'react';
import { Card, Btn, Note, KV, Yours, Tag, Select, Textarea, ErrorLine } from './kit';
import { Countdown } from '../surface';
import { money, whenLabel, dateLabel } from '../../utils/surfaceFormat';
import PromoCodesSection from '../PromoCodesSection';
import eventService from '../../services/eventService';
import feedbackService from '../../services/feedbackService';
import { useDialog } from '../../context';
import { escrowReleaseMoment } from './hostOpsModel';

const GREEN = '#16A34A';
const RED = '#B91C1C';
const KIND_COLOR = { sale: GREEN, release: GREEN, refund: RED, fee: '#000' };

/** Card head: 13px title on the left, a 10px faint word or a tag on the right. */
function Head({ title, right }) {
  return (
    <div className="hev-sec" style={{ alignItems: 'center' }}>
      <span className="hev-sec-title">{title}</span>
      {right}
    </div>
  );
}

function WhereTheMoneyIs({ event, moneyDto }) {
  const m = moneyDto;
  const starter = (m?.fundingType || event?.fundingType) === 'party_starter';
  const release = escrowReleaseMoment(event, m);
  const released = release ? release <= new Date() : false;
  const rows = [
    { k: 'Ticket revenue', v: money(m?.ticketRevenueCents) },
  ];
  if (m?.refundedCents > 0) rows.push({ k: 'Refunded', v: money(-m.refundedCents), color: RED });
  if (starter) {
    rows.push({ k: 'Party Starter fee (7%)', v: money(-(m?.hostFeeCents || 0)) });
    rows.push({ k: 'GST on the fee (5%)', v: money(-(m?.hostFeeGstCents || 0)) });
  }
  return (
    <Card gap="12" testId="money-summary">
      <Head title="WHERE THE MONEY IS" right={m?.estimated ? <Tag>ESTIMATED FROM SALES</Tag> : (m?.currency ? <span className="hev-caps-muted">{m.currency}</span> : null)} />
      {rows.map((r) => <KV key={r.k} k={r.k} v={r.v} color={r.color} />)}
      {!starter && (
        <span style={{ fontSize: 10, lineHeight: 1.5, color: '#9CA3AF' }}>Guests pay the $1 service fee, GST and card processing on top of the ticket — you are credited the sticker price.</span>
      )}
      <Yours value={money(m?.netToHostCents)} testId="money-yours" />
      {(m?.escrowHeldCents > 0 || m?.releasedCents > 0) && (
        <div style={{ background: '#F5F5F5', borderRadius: 18, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <KV small k="In escrow now" v={money(m?.escrowHeldCents || 0)} color={GREEN} />
          {m?.releasedCents > 0 && <KV small k="Released to your wallet" v={money(m.releasedCents)} />}
        </div>
      )}
      <Note tone="grey" tag="ESCROW">
        {released
          ? <>Released to your wallet {release ? `on ${whenLabel(release)}` : ''}.</>
          : release
            ? <>Everything sits with Portal until <b>{whenLabel(release)}</b> — 24h after doors — then lands in your wallet automatically.</>
            : <>Everything sits with Portal until 24h after doors, then lands in your wallet automatically.</>}
      </Note>
    </Card>
  );
}

function Ledger({ lines }) {
  if (!lines?.length) return null;
  return (
    <Card gap="12" testId="money-ledger">
      <Head title="EVERY TRANSACTION" right={<span className="hev-caps-muted">{lines.length} {lines.length === 1 ? 'ENTRY' : 'ENTRIES'}</span>} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {lines.map((l, i) => (
          <div key={`${l.at}-${i}`} className="hev-line" data-first={i === 0 ? '' : undefined}>
            <span style={{ flex: 'none', width: 64, fontSize: 11, color: '#9CA3AF' }}>{dateLabel(l.at)}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[l.who, l.what].filter(Boolean).join(' · ')}</span>
            <span style={{ flex: 'none', width: 82, textAlign: 'right', fontSize: 12, fontWeight: 700, color: KIND_COLOR[l.kind] || '#000', whiteSpace: 'nowrap' }}>{money(l.amountCents, { signed: l.kind === 'sale' || l.kind === 'release' })}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Post-event: escrow release, verification reports, REPORT AN ISSUE, host + guest feedback. */
function PostEvent({ event, eventId }) {
  const { showAlert } = useDialog();
  const [verifications, setVerifications] = useState([]);
  const [hostFeedback, setHostFeedback] = useState(null);
  const [guestFeedbacks, setGuestFeedbacks] = useState([]);
  const [verifyReason, setVerifyReason] = useState('');
  const [verifyDetails, setVerifyDetails] = useState('');
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [wouldInviteAgain, setWouldInviteAgain] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [verResult, fbResult] = await Promise.all([eventService.getVerifications(eventId), feedbackService.getEventFeedback(eventId)]);
      if (cancelled) return;
      if (verResult.success) setVerifications(verResult.data || []);
      if (fbResult.success) {
        const fbs = fbResult.data || [];
        setGuestFeedbacks(fbs.filter((f) => f.role === 'guest'));
        const mine = fbs.find((f) => f.role === 'host');
        if (mine) { setHostFeedback(mine); setWouldInviteAgain(mine.wouldInviteAgain); setFeedbackComment(mine.comment || ''); }
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const releaseAt = event?.escrowReleaseAt ? new Date(event.escrowReleaseAt) : null;
  const beforeRelease = releaseAt && releaseAt > new Date();

  const submitReport = async () => {
    if (!verifyReason) return;
    setVerifySubmitting(true); setError(null);
    try {
      const result = await eventService.verifyEvent(eventId, { type: 'host_report', reason: verifyReason, details: verifyDetails || undefined });
      if (result.success) { setVerifications((prev) => [...prev, result.data]); setVerifyReason(''); setVerifyDetails(''); }
      else setError(result.error || 'Failed to submit report');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message || 'Failed to submit report', type: 'error' });
    } finally { setVerifySubmitting(false); }
  };

  const submitFeedback = async () => {
    if (wouldInviteAgain === null) return;
    setFeedbackSubmitting(true); setError(null);
    try {
      const result = await feedbackService.submitFeedback(eventId, { wouldInviteAgain, comment: feedbackComment || undefined });
      if (result.success) setHostFeedback(result.data);
      else setError(result.error || 'Failed to submit feedback');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message || 'Failed to submit feedback', type: 'error' });
    } finally { setFeedbackSubmitting(false); }
  };

  return (
    <>
      {event?.escrowReleaseAt && (
        <Card black gap="10" testId="escrow-release" style={{ borderRadius: 24 }}>
          <div className="hev-rail-head">
            <span className="hev-rail-title">ESCROW RELEASE</span>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em', color: '#00FFFF', whiteSpace: 'nowrap' }}>
              {beforeRelease
                // eslint-disable-next-line react/style-prop-object -- Countdown's `style` is its format, not CSS
                ? <Countdown to={event.escrowReleaseAt} style="long" suffix=" LEFT" expiredText="RELEASED" />
                : 'RELEASED'}
            </span>
          </div>
          <span style={{ fontSize: 12, lineHeight: 1.6, color: '#D1D5DB' }}>
            {beforeRelease ? <>Funds release {whenLabel(event.escrowReleaseAt)}. Report issues before then.</> : <span style={{ color: '#22C55E', fontWeight: 700 }}>FUNDS RELEASED</span>}
          </span>
        </Card>
      )}

      {verifications.length > 0 && (
        <Card gap="12" testId="verification-reports">
          <Head title={`VERIFICATION REPORTS · ${verifications.length}`} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {verifications.map((v, i) => (
              <div key={v.id || i} className="hev-line" data-first={i === 0 ? '' : undefined} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4, padding: '12px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <Tag small style={{ background: '#FEE2E2', color: RED }}>{v.type === 'guest_report' ? 'GUEST REPORT' : 'HOST REPORT'}</Tag>
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>{dateLabel(v.createdAt)}</span>
                </div>
                <span style={{ fontSize: 13 }}>{v.reason}</span>
                {v.details && <span style={{ fontSize: 12, lineHeight: 1.6, color: '#6B7280' }}>{v.details}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {!verifications.some((v) => v.type === 'host_report') && beforeRelease && (
        <Card gap="12" testId="report-issue">
          <Head title="REPORT AN ISSUE" right={<Tag>BEFORE RELEASE</Tag>} />
          <Select value={verifyReason} onChange={(e) => setVerifyReason(e.target.value)} aria-label="Reason">
            <option value="">Select reason...</option>
            <option value="no_show">Guest no-shows</option>
            <option value="misconduct">Guest misconduct</option>
            <option value="damage">Property damage</option>
            <option value="other">Other issue</option>
          </Select>
          <Textarea value={verifyDetails} onChange={(e) => setVerifyDetails(e.target.value)} placeholder="Describe the issue (optional)" rows={3} aria-label="Details" style={{ minHeight: 100 }} />
          <Btn size="lg" self="start" onClick={submitReport} disabled={!verifyReason || verifySubmitting}>{verifySubmitting ? 'SUBMITTING…' : 'SUBMIT REPORT'}</Btn>
        </Card>
      )}

      <Card gap="12" testId="host-feedback">
        <Head title="POST-EVENT FEEDBACK" right={hostFeedback ? <Tag>SUBMITTED</Tag> : null} />
        {hostFeedback ? (
          <div style={{ background: '#F5F5F5', borderRadius: 18, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="hev-caps-muted">YOUR FEEDBACK</span>
            <span style={{ fontSize: 13 }}>Would invite guests again: <strong>{hostFeedback.wouldInviteAgain ? 'YES' : 'NO'}</strong></span>
            {hostFeedback.comment && <span style={{ fontSize: 12, lineHeight: 1.6, color: '#6B7280' }}>{hostFeedback.comment}</span>}
          </div>
        ) : (
          <>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Would you invite these guests again?</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant={wouldInviteAgain === true ? 'primary' : 'secondary'} style={{ flex: 1 }} onClick={() => setWouldInviteAgain(true)} aria-pressed={wouldInviteAgain === true}>YES</Btn>
              <Btn variant={wouldInviteAgain === false ? 'primary' : 'secondary'} style={{ flex: 1 }} onClick={() => setWouldInviteAgain(false)} aria-pressed={wouldInviteAgain === false}>NO</Btn>
            </div>
            <Textarea value={feedbackComment} onChange={(e) => setFeedbackComment(e.target.value)} placeholder="Any comments? (optional)" rows={3} aria-label="Feedback comment" style={{ minHeight: 100 }} />
            <Btn size="lg" self="start" onClick={submitFeedback} disabled={wouldInviteAgain === null || feedbackSubmitting}>{feedbackSubmitting ? 'SUBMITTING…' : 'SUBMIT FEEDBACK'}</Btn>
          </>
        )}
        <ErrorLine>{error}</ErrorLine>
        {guestFeedbacks.length > 0 && (
          <div style={{ borderTop: '2px solid #F2F2F2', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="guest-feedback">
            <span className="hev-caps-muted">GUEST FEEDBACK · {guestFeedbacks.length}</span>
            <span style={{ fontSize: 13 }}>Would attend again: <strong style={{ color: GREEN }}>{Math.round((guestFeedbacks.filter((f) => f.wouldAttendAgain).length / guestFeedbacks.length) * 100)}%</strong></span>
            {guestFeedbacks.filter((f) => f.comment).map((f) => (
              <div key={f.id} style={{ background: '#F5F5F5', borderRadius: 14, padding: '10px 14px', fontSize: 12, lineHeight: 1.6, color: '#6B7280' }}>{f.comment}</div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

export default function HostMoney({ event, eventId, moneyDto, mobile = false }) {
  const showPromo = !event?.isExternalTicketing && (event?.ticketPriceCents || 0) > 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="money-tab">
      {event?.status === 'canceled' && <Note tone="danger" title="THIS EVENT WAS CANCELED">All pledges have been refunded.</Note>}
      {event?.status === 'dissolved' && (
        <Note tone="grey">
          <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="hev-note-title">THIS PARTY STARTER DISSOLVED</span>
            <span>It did not fund by the deadline. Every pledge went back to the guest's wallet.</span>
          </span>
        </Note>
      )}
      <WhereTheMoneyIs event={event} moneyDto={moneyDto} />
      <Ledger lines={moneyDto?.lines} />
      {event?.status === 'ended' && <PostEvent event={event} eventId={eventId} />}
      {showPromo && (
        <div data-testid="promo-codes"><PromoCodesSection eventId={eventId} ticketPriceCents={event?.ticketPriceCents || 0} canManage={!!event?.viewerCanEdit} /></div>
      )}
    </div>
  );
}
